import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, Mail, Menu, X, Truck, Wrench } from "lucide-react";
import { PHONE, PHONE_HREF, EMAIL } from "@/lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/forklift-service-brisbane", label: "Service" },
  { to: "/forklift-maintenance-brisbane", label: "Maintenance" },
  { to: "/forklift-hire-brisbane", label: "Hire" },
  { to: "/forklifts-for-sale", label: "Sales" },
  { to: "/about-forkfix", label: "About" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  return (
    <>
      {/* Top contact bar */}
      <div className="hidden md:block bg-[#111111] text-white text-xs font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center">
          <span className="tracking-wide" data-testid="topbar-tagline">Brisbane's Local Forklift Specialist · Mobile Service & Same-Day Response</span>
          <div className="flex items-center gap-6">
            <a href={PHONE_HREF} className="flex items-center gap-2 hover:text-[#D71920]" data-testid="topbar-phone">
              <Phone className="w-3.5 h-3.5" /> {PHONE}
            </a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 hover:text-[#D71920]" data-testid="topbar-email">
              <Mail className="w-3.5 h-3.5" /> {EMAIL}
            </a>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#e5e5e5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/forkfix-logo.png" alt="Forkfix — Brisbane forklift service, hire and sales" className="h-12 md:h-14 w-auto object-contain" />
          </Link>
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) => `ff-nav-link text-[12px] xl:text-[13px] uppercase font-semibold tracking-wide whitespace-nowrap ${isActive ? "active text-[#D71920]" : "text-[#111]"}`}
                data-testid={`nav-${n.to.replace(/\//g, "-") || "home"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a href={PHONE_HREF} className="hidden md:inline-flex ff-btn-ghost" data-testid="header-call-btn">
              <Phone className="w-4 h-4" /> Call
            </a>
            <Link to="/book-service" className="ff-btn-primary" data-testid="header-book-btn">
              <Wrench className="w-4 h-4" /> Book Service
            </Link>
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2 border border-[#111]" aria-label="Menu" data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[#e5e5e5] bg-white">
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"}
                  className={({ isActive }) => `py-3 px-2 text-sm uppercase font-semibold tracking-wide border-l-2 ${isActive ? "border-[#D71920] text-[#D71920]" : "border-transparent"}`}
                  data-testid={`mnav-${n.to.replace(/\//g, "-") || "home"}`}>
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="min-h-[60vh] pb-24 md:pb-0">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#111111] text-white ff-diagonal-bg mt-16">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <img src="/forkfix-logo.png" alt="Forkfix" className="h-14 w-auto brightness-0 invert" />
            <p className="text-sm text-white/70 mt-5 max-w-sm">Brisbane's local forklift specialist — service, repairs, hire & sales. Mobile technicians, multi-brand experience, same-day response across Greater Brisbane.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold mb-3">Services</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/forklift-service-brisbane" className="hover:text-white">Forklift Service</Link></li>
              <li><Link to="/forklift-service-brisbane" className="hover:text-white">Forklift Repairs</Link></li>
              <li><Link to="/forklift-maintenance-brisbane" className="hover:text-white">Preventative Maintenance</Link></li>
              <li><Link to="/book-service" className="hover:text-white">Book Service</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold mb-3">Equipment</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/forklifts-for-sale" className="hover:text-white">Forklifts for Sale</Link></li>
              <li><Link to="/forklift-hire-brisbane" className="hover:text-white">Forklifts for Hire</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold mb-3">Contact</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li><a href={PHONE_HREF} className="hover:text-white">{PHONE}</a></li>
              <li><a href={`mailto:${EMAIL}`} className="hover:text-white">{EMAIL}</a></li>
              <li>Brisbane, QLD</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap justify-between text-xs text-white/60 gap-3">
            <span>© {new Date().getFullYear()} Forkfix. All Rights Reserved.</span>
            <span data-testid="footer-abn">ABN: 61 624 878 276</span>
          </div>
        </div>
      </footer>

      {/* Mobile sticky bottom bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 grid grid-cols-3 border-t border-[#e5e5e5] bg-white">
        <a href={PHONE_HREF} className="py-3 text-center text-[11px] uppercase tracking-wide font-bold border-r border-[#e5e5e5]" data-testid="sticky-call">
          <Phone className="w-4 h-4 mx-auto mb-1 text-[#D71920]" /> Call
        </a>
        <Link to="/book-service" className="py-3 text-center text-[11px] uppercase tracking-wide font-bold bg-[#D71920] text-white" data-testid="sticky-book">
          <Wrench className="w-4 h-4 mx-auto mb-1" /> Book Service
        </Link>
        <Link to="/contact" className="py-3 text-center text-[11px] uppercase tracking-wide font-bold border-l border-[#e5e5e5]" data-testid="sticky-quote">
          <Mail className="w-4 h-4 mx-auto mb-1 text-[#D71920]" /> Quote
        </Link>
      </div>
    </>
  );
}
